import { Check, Copy, Function, Info } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { useCopyToClipboard } from "hooks/useCopyToClipboard"
import Link from "next/link"
import { useMemo } from "react"
import { type ZoomValues } from "../Zoom/constants"

const calculateStartDate = (zoom: ZoomValues) => {
  if (zoom === 0) {
    return "2000-01-01"
  }
  if (zoom === -1) {
    const now = new Date()
    const year = now.getFullYear()
    return `${year}-01-01`
  }

  return new Date(Date.now() - zoom * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0]
}

function ChartFormulaLayout({
  onClose,
  formulas,
}: {
  formulas: string[]
  children?: React.ReactNode
  onClose?: () => void
}) {
  const formula = formulas.join("\n")

  const { copyText, copyToClipboard } = useCopyToClipboard({
    content: formulas.join(""),
    label: "Copy formulas",
  })

  return (
    <>
      <Modal.Header title="Sheets formula" icon={Function} />
      <div className="flex flex-col gap-6 max-w-full px-4 py-6">
        <div className="flex flex-col gap-5 max-w-full">
          <div className="text-sm text-fg-secondary">
            Copy the formula below into the Token Terminal Sheets plugin for{" "}
            <Link
              className="text-fg-emerald hover:underline"
              href="https://workspace.google.com/marketplace/app/token_terminal_for_sheets/702043887467"
              target="_blank"
            >
              Google Sheets
            </Link>{" "}
            or{" "}
            <Link
              className="text-fg-emerald hover:underline"
              href="https://appsource.microsoft.com/en-us/product/office/WA200007719"
              target="_blank"
            >
              Microsoft Excel
            </Link>
            .
          </div>
          <div className="flex items-center rounded-lg pl-4 pr-2 py-2 bg-neutral-100 dark:bg-neutral-900">
            <div className="relative flex-grow min-w-0">
              <code
                key={formula}
                className="relative flex flex-col items-start gap-4 text-sm font-mono pr-4 tracking-normal overflow-x-auto whitespace-nowrap no-scrollbar"
              >
                {formula}
              </code>
              <div className="absolute top-0 right-0 w-6 h-full bg-gradient-to-l from-neutral-100 dark:from-neutral-900 to-transparent" />
            </div>
            {/* We use opacity and absolute positioning so the content doesn't shift when we show the success state */}
            <div className="flex-shrink-0 relative">
              <Button
                variant="ghost"
                className={`pointer-events-none ${copyText === "Copied" ? "opacity-50" : "opacity-0"}`}
                excludeFromTabOrder
              >
                <Icon as={Check} />
                Copied
              </Button>
              <Button
                key={copyText}
                onPress={copyToClipboard}
                variant="ghost"
                className={`absolute top-0 right-0 ${copyText === "Copied" ? "opacity-0 pointer-events-none" : "opacity-100"}`}
              >
                <Icon as={Copy} />
                Copy
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Divider />
      <Modal.Footer className="justify-between pt-4">
        <LinkButton
          href="https://docs.tokenterminal.com/docs/sheets"
          target="_blank"
          variant="ghost"
          className="opacity-75 -ml-2 !px-3"
        >
          <Icon as={Info} />
          Learn more about formulas
        </LinkButton>
        <Button onPress={onClose}>Done</Button>
      </Modal.Footer>
    </>
  )
}

export function ChartFormulaFinancialStatementModalContent({
  onClose,
  granularity,
  project,
}: {
  project: string
  granularity: string
  onClose?: () => void
}) {
  const formulas = [
    `=TT_FINANCIAL_STATEMENT("${project}", "${granularity}", "max")`,
  ]

  return (
    <>
      <ChartFormulaLayout onClose={onClose} formulas={formulas} />
    </>
  )
}

export function ChartFormulaModalContent({
  series,
  onClose,
  zoom,
  singleProject,
}: {
  series: Array<{
    metrics: Array<string | undefined>
    projects: Array<string>
  }>
  onClose?: () => void
  zoom: ZoomValues
  singleProject?: boolean
}) {
  const formulas = useMemo(() => {
    return series.map(({ metrics, projects }) => {
      const startDate = calculateStartDate(zoom)

      if (singleProject) {
        // only used on projects page
        return `=TT_PROJECT_TIMESERIES("${projects.join(",")}",{${metrics
          .map((metric) => metric?.replaceAll("-", "_"))
          .map((metric) => `"${metric}"`)
          .join(",")}},"${startDate}")`
      } else {
        const tabCount = Array.from({ length: projects.length + 1 })
          .fill("	")
          .join("")

        return `=TT_TIMESERIES({${projects.map((project) => `"${project}"`).join(",")}},${metrics
          .map((metric) => metric?.replaceAll("-", "_"))
          .map((metric) => `"${metric}"`)
          .join(",")},"${startDate}")${tabCount}`
      }
    })
  }, [series, zoom, singleProject])

  return (
    <>
      <ChartFormulaLayout onClose={onClose} formulas={formulas} />
    </>
  )
}
